import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaArrowRight } from 'react-icons/fa';
import newsArticles, { toIsoDate } from '../../../components/news/newsarticles';
import Markdown from '../../../components/markdown/Markdown';
import NotFound from '../../notfound/NotFound';
import './newsarticle.css';

const SITE_URL = 'https://cyclesolutions.se';

const NewsArticle = () => {
    const { slug } = useParams();
    const article = newsArticles.find((a) => a.slug === slug && a.body);

    if (!article) {
        return <NotFound />;
    }

    const url = `${SITE_URL}/nyheter/${article.slug}`;
    const title = article.seoTitle || `${article.title} | Cycle Solutions`;
    const description = article.seoDescription || article.description;
    const imageUrl = article.image ? `${SITE_URL}${article.image}` : undefined;
    const isoDate = toIsoDate(article.date);
    const moreArticles = newsArticles.filter((a) => a.slug !== article.slug).slice(0, 3);

    const articleJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description,
        image: imageUrl ? [imageUrl] : undefined,
        datePublished: isoDate,
        dateModified: article.updated ? toIsoDate(article.updated) : isoDate,
        mainEntityOfPage: url,
        author: { '@type': 'Organization', name: 'Cycle Solutions Sverige AB', url: SITE_URL },
        publisher: {
            '@type': 'Organization',
            name: 'Cycle Solutions Sverige AB',
            logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo512.png` },
        },
    };

    const breadcrumbJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Hem', item: `${SITE_URL}/` },
            { '@type': 'ListItem', position: 2, name: 'Nyheter', item: `${SITE_URL}/nyheter` },
            { '@type': 'ListItem', position: 3, name: article.title, item: url },
        ],
    };

    return (
        <>
            <Helmet>
                <title>{title}</title>
                <meta name="description" content={description} />
                <link rel="canonical" href={url} />
                <meta property="og:type" content="article" />
                <meta property="og:title" content={article.title} />
                <meta property="og:description" content={description} />
                <meta property="og:url" content={url} />
                {imageUrl && <meta property="og:image" content={imageUrl} />}
                <meta property="article:published_time" content={isoDate} />
                <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
                <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
            </Helmet>

            <div className="news-article-page">
                <nav className="news-article-breadcrumbs" aria-label="Brödsmulor">
                    <Link to="/">Hem</Link>
                    <span aria-hidden="true">/</span>
                    <Link to="/nyheter">Nyheter</Link>
                </nav>

                <article className="news-article-content">
                    <header>
                        <h1>{article.title}</h1>
                        <p className="news-article-date">
                            <time dateTime={isoDate}>{article.date}</time>
                        </p>
                        {article.image && (
                            <img src={article.image} alt={article.title} className="news-article-image" />
                        )}
                        <p className="news-article-lead">{article.description}</p>
                    </header>

                    <Markdown text={article.body} />

                    <aside className="news-article-cta">
                        <h2>Vill du veta vilken lösning som passar er?</h2>
                        <p>
                            Våra säljare hjälper dig att hitta rätt balpress eller komprimator för era volymer och er yta.
                        </p>
                        <Link to="/kontakt" className="news-article-cta-button">Kontakta oss</Link>
                    </aside>
                </article>

                {moreArticles.length > 0 && (
                    <section className="news-article-more">
                        <h2>Fler nyheter</h2>
                        <ul>
                            {moreArticles.map((a) => (
                                <li key={a.slug}>
                                    <Link to={a.link}>
                                        {a.title} <FaArrowRight />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}
            </div>
        </>
    );
};

export default NewsArticle;
